import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridOptions,
    GridReadyEvent,
    ITooltipParams,
} from 'ag-grid-community';
import cloneDeep from 'lodash.clonedeep';
import { forkJoin, Observable, takeUntil, tap } from 'rxjs';
import {
    nameOf,
    padFrnWithLeadingZeros,
} from '../../../../core/functions/string-helpers';
import { BlockNavigationIfChange } from '../../../../core/models/block-navigation-if-change';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../core/models/form-group-type';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { FormEntryService } from '../../../../core/services/form-entry/form-entry.service';
import { ReturnRouteService } from '../../../../core/services/navigation/return-route.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { FRNResponse } from '../../../../core/services/uls/models/frn-response';
import { ULSService } from '../../../../core/services/uls/uls.service';
import { ValidationResult } from '../../../../core/services/validation/models/validation-result';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import {
    GridToolbarOptions,
    PrimaryGridAction,
} from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { ValidationErrorListComponent } from '../../../../shared/components/validation-error-list/validation-error-list.component';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { EmissionDesignatorSortingService } from '../../shared/services/emission-designator-sorting/emission-designator-sorting.service';
import { EmissionDesignatorSortModel } from '../../shared/services/emission-designator-sorting/models/emission-designator-sort-model';
import { StepNavigationService } from '../../shared/services/step-navigation/step-navigation.service';
import { ApplicationCoordinationBaseComponent } from '../shared/components/application-coordination-base/application-coordination-base.component';
import { CoordinationService } from '../shared/services/coordination/coordination.service';
import { BaseCoordinationParametersFrequencyFormModel } from './models/base-coordination-parameters-frequency-form-model';
import { CoordinationParametersFormModel } from './models/coordination-parameters-form-model';
import { CoordinationParametersRequest } from './models/coordination-parameters-request';
import { CoordinationParametersResponse } from './models/coordination-parameters-response';
import { FrequencyPoolFormModel } from './models/frequency-pool/frequency-pool-form-model';
import { IncludeFrequencyForValidationEmissionDesignator } from './models/include-frequency-for-validation/include-frequency-for-validation-emission-designator';
import { IncludeFrequencyForValidationFormModel } from './models/include-frequency-for-validation/include-frequency-for-validation-form-model';
import { CoordinationParametersFormService } from './services/coordination-parameters-form/coordination-parameters-form.service';
import { FrequencyPoolFormService } from './services/frequency-pool-form/frequency-pool-form.service';
import { IncludeFrequencyForValidationEmissionDesignatorFormService } from './services/include-frequency-for-validation-emission-designator-form/include-frequency-for-validation-emission-designator-form.service';
import { IncludeFrequencyForValidationFormService } from './services/include-frequency-for-validation-form/include-frequency-for-validation-form.service';
import { CoordinationParametersGridApiMap } from './types/coordination-parameters-grid-api-map';
import { CoordinationParametersGridType } from './types/coordination-parameters-grid-type';
import { CoordinationParametersRowDataMap } from './types/coordination-parameters-row-data-map.ts';

import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CheckboxCellRendererParams } from '../../../../core/models/grid/checkbox-cell-renderer-params';
import { FrequencyFormattingService } from '../../../../core/services/frequency-formatting/frequency-formatting.service';
import { ButtonCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/button-cell-renderer/button-cell-renderer.component';
import { ButtonCellRendererParams } from '../../../../shared/components/grid/cell-renderers/button-cell-renderer/models/button-cell-renderer-params';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { ApplicationFormControlManagerService } from '../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { CoordinationParametersErrorMessages } from './constants/coordination-parameters-error-messages';
import { CallSignsOrULSFileNumbersAddAction } from './models/call-signs-or-uls-file-numbers-add-action';
import { FriendlySelectionFormModel } from './models/friendly-selection/friendly-selection-form-model';
import { FriendlySelectionSearchForm } from './models/friendly-selection/friendly-selection-search-form';
import { FRNSearch } from './models/friendly-selection/frn-search';
import { FRNsSearchAction } from './models/friendly-selection/frns-search-action';
import { FriendlySelectionFormService } from './services/friendly-selection-form/friendly-selection-form.service';
import { FriendlySelectionSearchFormService } from './services/friendly-selection-search-form/friendly-selection-search-form.service';

@Component({
    selector: 'app-coordination-parameters',
    imports: [
        ApplicationFormFieldComponent,
        MatFormFieldModule,
        MatSelectModule,
        ReactiveFormsModule,
        MatInputModule,
        MatExpansionModule,
        AgGridAngular,
        MatChipsModule,
        MatIconModule,
        GridToolbarComponent,
        MatCheckboxModule,
        ValidationErrorListComponent,
        MatButtonModule,
        RegexInputDirective,
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatError,
        MatIconModule,
        RegexInputDirective,
        MatChipsModule,
        MatTooltipModule,
    ],
    styleUrls: ['./coordination-parameters.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './coordination-parameters.component.html',
})
export class CoordinationParametersComponent
    extends ApplicationCoordinationBaseComponent<
        CoordinationParametersRequest,
        CoordinationParametersResponse,
        CoordinationParametersFormModel
    >
    implements OnInit, OnDestroy, BlockNavigationIfChange
{
    applicationTypes: DropdownOption[] = [];
    frequencyAllocationCodes: DropdownOption[] = [];

    regExPattern = RegExPattern;

    lastFRNsSearchAction: FRNsSearchAction | null = null;
    lastCallSignsOrULSFileNumbersAddAction: CallSignsOrULSFileNumbersAddAction | null =
        null;

    coordinationParametersGridType = CoordinationParametersGridType;

    rowData: CoordinationParametersRowDataMap = {
        [CoordinationParametersGridType.frequencyPool]: [],
        [CoordinationParametersGridType.includeFrequenciesForValidation]: [],
        [CoordinationParametersGridType.friendlySelection]: [],
    };

    gridApis: CoordinationParametersGridApiMap =
        {} as CoordinationParametersGridApiMap;

    frequencyPoolGridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {
            this.updateGrid(CoordinationParametersGridType.frequencyPool);
        },
        quickFilter: false,
        clearFilters: true,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<FrequencyPoolFormModel>('frequencyAllocationCode'),
                nameOf<FrequencyPoolFormModel>('actionCode'),
                nameOf<FrequencyPoolFormModel>('locationNumber'),
                nameOf<FrequencyPoolFormModel>('locationTypeCode'),
                nameOf<FrequencyPoolFormModel>('antennaNumber'),
                nameOf<FrequencyPoolFormModel>('antennaHeightToTip'),
                nameOf<FrequencyPoolFormModel>('powerErp'),
                nameOf<FrequencyPoolFormModel>('frequencyLow'),
                nameOf<FrequencyPoolFormModel>('frequencyHigh'),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Frequency_Type',
                    this.applicationState?.salmonLogId,
                ),
        },
    };

    includeFrequenciesForValidationGridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {
            this.updateGrid(
                CoordinationParametersGridType.includeFrequenciesForValidation,
            );
        },
        quickFilter: false,
        clearFilters: true,
        exportCsv: true,
        exportCsvOptions: {
            columnKeys: [
                nameOf<IncludeFrequencyForValidationFormModel>(
                    'includeForValidation',
                ),
                nameOf<IncludeFrequencyForValidationFormModel>(
                    'locationNumber',
                ),
                nameOf<IncludeFrequencyForValidationFormModel>(
                    'locationTypeCode',
                ),
                nameOf<IncludeFrequencyForValidationFormModel>('antennaNumber'),
                nameOf<IncludeFrequencyForValidationFormModel>(
                    'antennaHeightToTip',
                ),
                nameOf<IncludeFrequencyForValidationFormModel>('powerErp'),
                nameOf<IncludeFrequencyForValidationFormModel>('frequencyLow'),
                nameOf<IncludeFrequencyForValidationFormModel>('frequencyHigh'),
                nameOf<IncludeFrequencyForValidationFormModel>(
                    'emissionDesignatorsListForExport',
                ),
            ],
            fileName: () =>
                GridHelper.getExportFileName(
                    'Include_Exclude',
                    this.applicationState?.salmonLogId,
                ),
        },
    };

    /**
     * Column definitions that are shared between the Frequency Pool and Include Frequencies for Validation grids.
     */
    commonFrequencyColumnDefs: ColDef[] = [
        {
            headerName: 'Location #',
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'locationNumber',
            ),
            maxWidth: 150,
        },
        {
            headerName: 'Location Desc.',
            wrapHeaderText: true,
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'locationTypeCode',
            ),
            minWidth: 140,
            maxWidth: 140,
        },
        {
            headerName: 'Antenna #',
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'antennaNumber',
            ),
            maxWidth: 170,
        },
        {
            headerName: 'Antenna Height',
            wrapHeaderText: true,
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'antennaHeightToTip',
            ),
            minWidth: 135,
        },
        {
            headerName: 'ERP',
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'powerErp',
            ),
            minWidth: 115,
        },
        {
            headerName: 'Frequency',
            // Field is not set to freq low or freq high so the export doesn't take the string formatted value
            // from this column
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'frequencyLabel',
            ),
            // Needed so AgGrid sorts on the frequencyLow value and not the frequencyLabel
            valueGetter: (params) => {
                return params.data.frequencyLow;
            },
            minWidth: 185,
            pinned: 'right',
            useValueFormatterForExport: false,
            valueFormatter: (params) => {
                return params.data
                    ? this.frequencyFormattingService.getFrequencyLabel(
                          params.data.frequencyLow,
                          params.data.frequencyHigh,
                      )
                    : '';
            },
        },

        ///
        /// Hidden columns, added for export
        ///

        {
            hide: true,
            headerName: 'Frequency Low',
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'frequencyLow',
            ),
        },
        {
            hide: true,
            headerName: 'Frequency High',
            field: nameOf<BaseCoordinationParametersFrequencyFormModel>(
                'frequencyHigh',
            ),
        },
    ];

    frequencyPoolColumnDefs: ColDef[] = [
        GridHelper.selectRowColDef,
        GridHelper.actionCodeColDef,
        ...this.commonFrequencyColumnDefs,

        ///
        /// Hidden columns, added for export
        ///

        {
            hide: true,
            headerName: 'Frequency Type',
            field: nameOf<FrequencyPoolFormModel>('frequencyAllocationCode'),
            valueFormatter: (params) => {
                const code = params.data?.frequencyAllocationCode;
                if (!code) {
                    return '';
                }

                const match = this.frequencyAllocationCodes.find(
                    (option) => option.value === code,
                );
                if (!match) {
                    throw new Error(`Frequency type for ${code} not found.`);
                }

                return match.label;
            },
        },
    ];

    frequencyPoolGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        columnDefs: this.frequencyPoolColumnDefs,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
    };

    includeFrequenciesForValidationColumnDefs: ColDef[] = [
        {
            ...GridHelper.selectRowColDef,
            maxWidth: 90,
            headerName: 'Include',
            field: nameOf<IncludeFrequencyForValidationFormModel>(
                'includeForValidation',
            ),
            cellRendererParams: {
                visuallyDisabled: (params: CheckboxCellRendererParams) => {
                    return params.data.isAutocoordApproved !== true;
                },
            },
            tooltipValueGetter: (params) => {
                return !params.data.isAutocoordApproved
                    ? 'This frequency is not approved for Coordination and cannot be included.'
                    : null;
            },
        },
        ...this.commonFrequencyColumnDefs,
        {
            headerName: 'Emission Designators',
            wrapHeaderText: true,
            field: nameOf<IncludeFrequencyForValidationFormModel>(
                'emissionDesignators',
            ),
            maxWidth: 200,
            sortable: false,
            filter: false,
            pinned: 'right',
            tooltipValueGetter: (params: ITooltipParams) => {
                const emissionDesignators: EmissionDesignatorSortModel[] =
                    params.data.emissionDesignators;

                if (emissionDesignators.length > 1) {
                    return this.emissionDesignatorSortingService
                        .sortByBandwidthDescending(emissionDesignators)
                        .map(
                            (emissionDesignator) =>
                                emissionDesignator.emissionDesignatorCode,
                        )
                        .join(', ');
                }

                // Do not show a tooltip if there is only one emission designator
                return null;
            },
            valueFormatter: (params) => {
                const emissionDesignators: IncludeFrequencyForValidationEmissionDesignator[] =
                    params.data.emissionDesignators;

                if (emissionDesignators.length > 1) {
                    return `${this.emissionDesignatorSortingService.sortByBandwidthDescending(emissionDesignators)[0].emissionDesignatorCode}...`;
                } else {
                    return emissionDesignators[0].emissionDesignatorCode;
                }
            },
            useValueFormatterForExport: false,
        },

        ///
        /// Hidden columns, added for export
        ///

        {
            hide: true,
            headerName: 'Emission Designators',
            field: nameOf<IncludeFrequencyForValidationFormModel>(
                'emissionDesignatorsListForExport',
            ),
            valueFormatter: (params) => {
                const emissionDesignators: IncludeFrequencyForValidationEmissionDesignator[] =
                    params.data.emissionDesignators;

                return emissionDesignators
                    .map(
                        (emissionDesignator) =>
                            emissionDesignator.emissionDesignatorCode,
                    )
                    .join(',');
            },
        },
    ];

    includeFrequenciesForValidationGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        ...GridHelper.getFormCellRendererOnCellFocusedGridOptions(),
        columnDefs: this.includeFrequenciesForValidationColumnDefs,
    };

    friendliesGridToolbarOptions: GridToolbarOptions = {
        refreshDataForExport: () => {},
        quickFilter: true,
        exportCsv: false,
        primaryGridAction: this.friendliesGridToolbarOptionPrimaryGridAction(),
    };

    friendliesColumnDefs: ColDef[] = [
        {
            headerName: 'Call Sign/ULS File Number',
            field: nameOf<FriendlySelectionFormModel>('identifier'),
            filter: false,
            sort: 'asc',
        },
        {
            headerName: 'Delete',
            maxWidth: 100,
            filter: false,
            sortable: false,
            cellRenderer: ButtonCellRendererComponent,
            cellRendererParams: {
                classList: '!rounded-full',
                disabled: this.isReadOnly(),
                matIconIdentifier: 'clear',
                iconClassList: '!m-0 !text-white',
                clickFunction: (params: ButtonCellRendererParams) =>
                    this.deleteFriendlySelection(params),
            },
        },
    ];

    friendlySelectionGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        columnDefs: this.friendliesColumnDefs,
        paginationPageSize: 150,
    };

    private readonly coordinationService = inject(CoordinationService);
    private readonly dropdownService = inject(DropdownService);
    private readonly formService = inject(CoordinationParametersFormService);
    private readonly frequencyPoolFormService = inject(
        FrequencyPoolFormService,
    );
    private readonly includeFrequencyForValidationFormService = inject(
        IncludeFrequencyForValidationFormService,
    );
    private readonly includeFrequencyForValidationEmissionDesignatorFormService =
        inject(IncludeFrequencyForValidationEmissionDesignatorFormService);
    private readonly friendlySelectionFormService = inject(
        FriendlySelectionFormService,
    );
    private readonly friendlySelectionSearchFormService = inject(
        FriendlySelectionSearchFormService,
    );
    private readonly emissionDesignatorSortingService = inject(
        EmissionDesignatorSortingService,
    );
    private readonly ulsService = inject(ULSService);
    private readonly dialog = inject(MatDialog);
    private readonly frequencyFormattingService = inject(
        FrequencyFormattingService,
    );

    override form: FormGroup<
        NestedFormGroupType<CoordinationParametersFormModel>
    > = this.formService.getFormDefinition();

    friendlySelectionSearchForm: FormGroup<
        NestedFormGroupType<FriendlySelectionSearchForm>
    > = this.friendlySelectionSearchFormService.getFormDefinition();

    constructor() {
        super(
            inject(ActivatedRoute),
            inject(StepNavigationService),
            inject(Router),
            inject(FormEntryService),
            inject(ApplicationStateService),
            inject(ApplicationFormControlManagerService),
            inject(ReturnRouteService),
        );
    }

    get applicationTypeCodeControl(): FormControl<string | null> {
        return this.form.controls.applicationTypeCode as FormControl<
            string | null
        >;
    }

    get frequencyAllocationCodeControl(): FormControl<string | null> {
        return this.form.controls.frequencyAllocationCode as FormControl<
            string | null
        >;
    }

    get frequencyPoolOptionsControl(): FormArray<
        FormGroup<FormGroupType<FrequencyPoolFormModel>>
    > {
        return this.form.controls.frequencyPoolOptions as FormArray<
            FormGroup<FormGroupType<FrequencyPoolFormModel>>
        >;
    }

    get includeFrequenciesForValidationControl(): FormArray<
        FormGroup<NestedFormGroupType<IncludeFrequencyForValidationFormModel>>
    > {
        return this.form.controls.includeFrequenciesForValidation as FormArray<
            FormGroup<
                NestedFormGroupType<IncludeFrequencyForValidationFormModel>
            >
        >;
    }

    get friendlySelectionControl(): FormArray<
        FormGroup<FormGroupType<FriendlySelectionFormModel>>
    > {
        return this.form.controls.friendlySelection as FormArray<
            FormGroup<FormGroupType<FriendlySelectionFormModel>>
        >;
    }

    get friendlySelectionSearchFormFrnSearchControl(): FormControl<
        string | null
    > {
        return this.friendlySelectionSearchForm.controls
            .frnSearch as FormControl<string | null>;
    }

    get friendlySelectionSearchFormAddCallSignOrULSFileNumberControl(): FormControl<
        string | null
    > {
        return this.friendlySelectionSearchForm.controls
            .addCallSignOrULSFileNumber as FormControl<string | null>;
    }

    get friendlySelectionSearchFormFRNSearchHistoryControl(): FormArray<
        FormGroup<FormGroupType<FRNSearch>>
    > {
        return this.friendlySelectionSearchForm.controls
            .frnSearchHistory as FormArray<FormGroup<FormGroupType<FRNSearch>>>;
    }

    formatFrnForDisplay(frn: string | null | undefined): string | null {
        return padFrnWithLeadingZeros(frn);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(gridType: CoordinationParametersGridType): any {
        let formArray;
        switch (gridType) {
            case CoordinationParametersGridType.frequencyPool:
                formArray = this.frequencyPoolOptionsControl;
                break;
            case CoordinationParametersGridType.includeFrequenciesForValidation:
                formArray = this.includeFrequenciesForValidationControl;
                break;
            case CoordinationParametersGridType.friendlySelection:
                formArray = this.friendlySelectionControl;
                break;
            default:
                throw new Error('Invalid grid type');
        }

        return {
            gridType: gridType,
            formArray: formArray,
        };
    }

    onGridReady(params: GridReadyEvent, type: CoordinationParametersGridType) {
        this.gridApis[type] = params.api;
        this.updateGrid(type);
    }

    updateGrid(type: CoordinationParametersGridType) {
        let data = [];
        switch (type) {
            case CoordinationParametersGridType.frequencyPool:
                data = cloneDeep(this.frequencyPoolOptionsControl.value);
                break;
            case CoordinationParametersGridType.includeFrequenciesForValidation:
                data = cloneDeep(
                    this.includeFrequenciesForValidationControl.value,
                );
                break;
            case CoordinationParametersGridType.friendlySelection:
                data = cloneDeep(this.friendlySelectionControl.value);
                break;
            default:
                throw new Error('Invalid grid type');
        }

        if (this.gridApis[type]) {
            this.gridApis[type].setGridOption('rowData', data);
        }

        return;
    }

    isFrequencyPoolSectionInvalid(): boolean {
        return (
            this.applicationTypeCodeControl.invalid ||
            this.frequencyAllocationCodeControl.invalid ||
            this.frequencyPoolOptionsControl.invalid
        );
    }

    isIncludeFrequenciesForValidationSectionInvalid(): boolean {
        return this.includeFrequenciesForValidationControl.invalid;
    }

    getFRNSearchHint(): string | undefined {
        return this.friendlySelectionSearchFormService.getFRNSearchHint(
            this.lastFRNsSearchAction,
        );
    }

    getAddCallSignOrULSFileNumberHint(): string | undefined {
        return this.friendlySelectionSearchFormService.getAddCallSignOrULSFileNumberHint(
            this.lastCallSignsOrULSFileNumbersAddAction,
        );
    }

    getFRNSearchError(): string | undefined {
        return this.friendlySelectionSearchFormService.getFRNSearchError(
            this.friendlySelectionSearchForm,
        );
    }

    getAddCallSignOrULSFileNumberError(): string | undefined {
        return this.friendlySelectionSearchFormService.getAddCallSignOrULSFileNumberError(
            this.friendlySelectionSearchForm,
        );
    }

    /**
     * Searches for friendlies based on the FRNs input in the FRN search input field.
     * Will call the ULS service to get friendlies for the FRNs and add them to the friendly selection form array.
     * If the input is empty or invalid, will not search.
     * Updates the FRN search hint and error messages based on the search results.
     * @returns
     */
    searchFRNFriendlies(): void {
        const frnsInput =
            this.friendlySelectionSearchForm.controls.frnSearch.value;
        if (!frnsInput) {
            return;
        }

        // Get the list of FRNs to search from the input value. The input will be parsed and split into individual FRNs.
        const frns: string[] =
            this.friendlySelectionSearchFormService.getFRNsToSearchFromInputValue(
                frnsInput,
            );

        // Remove duplicate FRNs to ensure each FRN is searched only once.
        const uniqueFRNs = frns.filter(
            (frn, index) => frns.indexOf(frn) === index,
        );

        // Filter out FRNs that are longer than 10 digits, as they are considered invalid for the ULS service request
        const uniqueAndValidFRNs = uniqueFRNs.filter((frn) => frn.length <= 10);

        // If all FRNs are invalid, show an error and stop the search
        if (uniqueAndValidFRNs.length === 0) {
            this.handleFRNSearchError(uniqueFRNs);

            return;
        }

        // Convert the valid FRNs to numerical values for the ULS service request
        const numericalUniqueAndValidFRNs = uniqueAndValidFRNs.map((frn) =>
            parseInt(frn, 10),
        );

        this.ulsService
            .getFriendliesForFRNs({
                frns: numericalUniqueAndValidFRNs,
            })
            .pipe(
                takeUntil(this.destroy$),
                tap((response: FRNResponse[]) => {
                    const uniqueFRNsNotFound = uniqueFRNs.filter(
                        (frn) =>
                            !response.some(
                                (friendly) => friendly.frn.toString() === frn,
                            ),
                    );

                    // If any of the searched FRNs were not found in the response, show an error and stop the search
                    if (uniqueFRNsNotFound.length > 0) {
                        this.handleFRNSearchError(uniqueFRNsNotFound);

                        return;
                    }

                    this.addFRNsToSearchHistoryList(
                        numericalUniqueAndValidFRNs,
                    );

                    const friendliesToAdd: FriendlySelectionFormModel[] = [];

                    // Add both the Call Sign and the ULS File Number to friendlies if they exist
                    response.forEach((friendlyResponse) => {
                        const callSign = friendlyResponse.callSign;
                        const ulsFileNumber = friendlyResponse.ulsFileNumber;

                        [callSign, ulsFileNumber].forEach((identifier) => {
                            if (identifier) {
                                const friendly: FriendlySelectionFormModel = {
                                    identifier: identifier,
                                };

                                friendliesToAdd.push(friendly);
                            }
                        });
                    });

                    const result =
                        this.friendlySelectionFormService.addAllToFriendlySelection(
                            this.friendlySelectionControl,
                            friendliesToAdd,
                        );

                    this.resetFRNSearchFormValue();

                    this.lastFRNsSearchAction = {
                        frnsSearched: numericalUniqueAndValidFRNs,
                        friendliesAdded: result.friendliesAdded,
                        friendliesAlreadyAdded: result.friendliesAlreadyAdded,
                    };

                    if (this.lastFRNsSearchAction.friendliesAdded > 0) {
                        this.updateGrid(
                            CoordinationParametersGridType.friendlySelection,
                        );
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Will clear out the list of previously searched FRNs but not remove the friendlies from the friendly selection form array.
     */
    clearFRNSearchHistoryList(): void {
        this.friendlySelectionSearchFormFRNSearchHistoryControl.clear();
    }

    /**
     * Adds call signs or ULS file numbers to the friendly selection form array based on the input in the add call sign or ULS file number input field.
     * Will add the identifiers to the friendly selection form array if they are not already added.
     * If the input is empty or invalid, will not add.
     * Updates the add call sign or ULS file number hint and error messages based on the add results.
     * @returns
     */
    addCallSignsOrULSFileNumbersToFriendlies(): void {
        const callSignOrULSFileNumberControlValue =
            this.friendlySelectionSearchForm.controls.addCallSignOrULSFileNumber
                .value;
        if (!callSignOrULSFileNumberControlValue) {
            return;
        }

        const identifiersToAdd =
            this.friendlySelectionSearchFormService.getCallSignsOrULSFileNumbersToAddFromInputValue(
                callSignOrULSFileNumberControlValue,
            );

        if (identifiersToAdd.some((identifier) => identifier.length > 10)) {
            this.handleAddCallSignOrFileNumberError('invalidLength');
            return;
        }

        const addCallSignsOrULSFileNumbersAction: CallSignsOrULSFileNumbersAddAction =
            {
                callSignsOrULSFileNumbers: identifiersToAdd,
                friendliesAdded: 0,
                friendliesAlreadyAdded: 0,
            };

        const friendliesToAdd: FriendlySelectionFormModel[] =
            identifiersToAdd.map((identifier) => {
                return {
                    identifier: identifier,
                };
            });

        const result =
            this.friendlySelectionFormService.addAllToFriendlySelection(
                this.friendlySelectionControl,
                friendliesToAdd,
            );

        addCallSignsOrULSFileNumbersAction.friendliesAdded =
            result.friendliesAdded;
        addCallSignsOrULSFileNumbersAction.friendliesAlreadyAdded =
            result.friendliesAlreadyAdded;

        this.resetCallSignOrULSFileNumberFormValue();

        this.lastCallSignsOrULSFileNumbersAddAction =
            addCallSignsOrULSFileNumbersAction;

        if (addCallSignsOrULSFileNumbersAction.friendliesAdded > 0) {
            this.updateGrid(CoordinationParametersGridType.friendlySelection);
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
    }

    /**
     * Disables the friendly selection search form if the form is in read-only mode.
     */
    private updateFriendlySelectionFormEnabledState(): void {
        if (this.isReadOnly()) {
            this.friendlySelectionSearchForm.disable({ emitEvent: false });
        }
    }

    private resetCallSignOrULSFileNumberFormValue(): void {
        this.friendlySelectionSearchFormAddCallSignOrULSFileNumberControl.setValue(
            '',
        );
    }

    private resetFRNSearchFormValue(): void {
        this.friendlySelectionSearchFormFrnSearchControl.setValue('');
    }

    private handleFRNSearchError(uniqueFRNsNotFound: string[]): void {
        if (uniqueFRNsNotFound.length === 0) {
            throw Error('No FRNs not found to handle.');
        }

        const notFoundErrorMessage =
            CoordinationParametersErrorMessages.friendlies.frnSearch.notFound(
                uniqueFRNsNotFound.join(', '),
            );

        this.friendlySelectionSearchForm.controls.frnSearch?.setErrors({
            notFound: notFoundErrorMessage,
        });

        this.lastFRNsSearchAction = null;
    }

    private handleAddCallSignOrFileNumberError(
        errorKey: keyof typeof CoordinationParametersErrorMessages.friendlies.addCallSignOrULSFileNumber,
    ): void {
        this.friendlySelectionSearchForm.controls.addCallSignOrULSFileNumber?.setErrors(
            {
                [errorKey]:
                    CoordinationParametersErrorMessages.friendlies
                        .addCallSignOrULSFileNumber[errorKey],
            },
        );

        this.lastCallSignsOrULSFileNumbersAddAction = null;
    }

    private addFRNsToSearchHistoryList(frns: number[]): void {
        frns.forEach((frn) => {
            if (this.frnInSearchHistory(frn)) {
                return;
            }

            const frnResponseForm =
                this.friendlySelectionSearchFormService.getFRNsSearchHistoryFormDefinition();
            frnResponseForm.controls.frn.setValue(frn.toString());

            this.friendlySelectionSearchFormFRNSearchHistoryControl.push(
                frnResponseForm,
            );
        });
    }

    private frnInSearchHistory(frn: number): boolean {
        return this.friendlySelectionSearchFormFRNSearchHistoryControl.controls.some(
            (control) => control.controls.frn.value === frn.toString(),
        );
    }

    private openDeleteAllFriendliesDialog(): void {
        if (this.dialog.openDialogs.length > 0) {
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: 'Are you sure you want to delete all friendlies?',
                primaryAction: {
                    title: 'Delete All',
                    classList: '!bg-primary-700 !text-tertiary-50',
                    action: this.deleteAllFriendlies.bind(this),
                },
            },
        });
    }

    private deleteAllFriendlies(): void {
        this.friendlySelectionControl.clear();
        this.clearFRNSearchHistoryList();
        this.updateGrid(CoordinationParametersGridType.friendlySelection);
    }

    /**
     * Deletes a friendly from the friendlies form array.
     * @param params The params from the button cell renderer that contains the row index of the friendly to delete.
     */
    private deleteFriendlySelection(params: ButtonCellRendererParams): void {
        const rowIndex = params;

        if (rowIndex !== null) {
            this.friendlySelectionControl.value.findIndex((friendly, index) => {
                if (friendly.identifier === params.data.identifier) {
                    this.friendlySelectionControl.removeAt(index);
                    this.updateGrid(
                        CoordinationParametersGridType.friendlySelection,
                    );
                }
            });
        }
    }

    private setupFormHandlers(): void {
        // Frequency Pool section handlers
        this.handleFrequencyAllocationCodeChange();
        this.handleFrequencyPoolOptionIsSelectedChange();

        // Include Frequencies for Validation section handlers
        this.handleIncludeForValidationChange();

        this.setupFriendlySelectionSearchFormHandlers();
    }

    private setupFriendlySelectionSearchFormHandlers(): void {
        this.friendlySelectionSearchFormFrnSearchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    this.friendlySelectionSearchFormService.replaceNewLinesWithDelimeter(
                        this.friendlySelectionSearchFormFrnSearchControl,
                        value,
                    );
                }),
            )
            .subscribe();

        this.friendlySelectionSearchFormAddCallSignOrULSFileNumberControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    this.friendlySelectionSearchFormService.replaceNewLinesWithDelimeter(
                        this
                            .friendlySelectionSearchFormAddCallSignOrULSFileNumberControl,
                        value,
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Will listen for changes to the frequency allocation code control and if the control value is null then
     * it will deselect all frequency rows and disable the checkboxes, otherwise it will enable the checkboxes.
     */
    private handleFrequencyAllocationCodeChange(): void {
        this.frequencyAllocationCodeControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => {
                    this.frequencyPoolOptionsControl.controls.forEach(
                        (formGroup) => {
                            const isSelectedControl =
                                formGroup.controls.isSelected;

                            if (!value) {
                                isSelectedControl.setValue(false);
                                isSelectedControl.disable();
                            } else {
                                isSelectedControl.enable();
                            }
                        },
                    );

                    this.gridApis.frequencyPool.redrawRows();
                }),
            )
            .subscribe();
    }

    /**
     * Will listen for changes to the isSelected control of each frequency pool option and if the value is true
     * it will set the frequency's allocation code to the value of the frequency allocation code control, othwerwise
     * it will set the frequency's allocation code to null.
     */
    private handleFrequencyPoolOptionIsSelectedChange(): void {
        this.frequencyPoolOptionsControl.controls.forEach((formGroup) => {
            formGroup.controls.isSelected.valueChanges
                .pipe(
                    takeUntil(this.destroy$),
                    tap((value) => {
                        const newFrequencyAllocationValue =
                            value === true
                                ? this.frequencyAllocationCodeControl.value
                                : null;
                        formGroup.controls.frequencyAllocationCode.setValue(
                            newFrequencyAllocationValue,
                        );

                        this.gridApis[
                            CoordinationParametersGridType.frequencyPool
                        ]?.redrawRows();
                    }),
                )
                .subscribe();
        });
    }

    /**
     * Will listen for changes to the includeForValidation control group array and if the value changes
     * it will redraw the grid to show validation issues.
     */
    private handleIncludeForValidationChange(): void {
        this.includeFrequenciesForValidationControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    const includedFrequencies =
                        this.includeFrequenciesForValidationControl.controls.filter(
                            (control) =>
                                control.value.includeForValidation === true,
                        );

                    // Only redraw if there are 0 or 1 included frequencies
                    // which means the validation state may have changed
                    if (includedFrequencies.length < 2) {
                        this.gridApis[
                            CoordinationParametersGridType
                                .includeFrequenciesForValidation
                        ]?.redrawRows();
                    }
                }),
            )
            .subscribe();
    }

    private loadDropdownData(): void {
        forkJoin({
            applicationTypes: this.dropdownService.getApplicationTypeOptions(),
            frequencyAllocationCodes:
                this.dropdownService.getFrequencyAllocationOptions(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap((result) => {
                    this.applicationTypes = result.applicationTypes;
                    this.frequencyAllocationCodes =
                        result.frequencyAllocationCodes;
                }),
            )
            .subscribe();
    }

    private addFrequencyPoolFormDefinition(): void {
        this.frequencyPoolOptionsControl.push(
            this.frequencyPoolFormService.getFormDefinition(),
        );
    }

    private addIncludeExcludeFrequencyFormDefinition(): void {
        this.includeFrequenciesForValidationControl.push(
            this.includeFrequencyForValidationFormService.getFormDefinition(),
        );
    }

    private addEmissionDesignatorsFormDefinition(frequencyIndex: number): void {
        const emissionDesignators =
            this.includeFrequenciesForValidationControl.at(frequencyIndex)
                .controls.emissionDesignators as FormArray;

        emissionDesignators.push(
            this.includeFrequencyForValidationEmissionDesignatorFormService.getFormDefinition(),
        );
    }

    private addFriendlySelectionFormDefinition(): void {
        this.friendlySelectionControl.push(
            this.friendlySelectionFormService.getFormDefinition(),
        );
    }

    private friendliesGridToolbarOptionPrimaryGridAction():
        PrimaryGridAction | undefined {
        if (this.isReadOnly()) {
            return;
        }

        return {
            title: 'Delete All',
            action: () => this.openDeleteAllFriendliesDialog(),
        };
    }

    protected override postFormBindingActions(): void {
        this.updateFriendlySelectionFormEnabledState();
    }

    protected override bindForm(
        response: CoordinationParametersResponse,
    ): void {
        response.frequencyPoolOptions.forEach(() => {
            this.addFrequencyPoolFormDefinition();
        });

        response.includeFrequenciesForValidation.forEach(
            (includeFrequencyForValidation) => {
                this.addIncludeExcludeFrequencyFormDefinition();

                includeFrequencyForValidation.emissionDesignators.forEach(
                    () => {
                        this.addEmissionDesignatorsFormDefinition(
                            response.includeFrequenciesForValidation.indexOf(
                                includeFrequencyForValidation,
                            ),
                        );
                    },
                );
            },
        );

        response.friendlySelection.forEach(() => {
            this.addFriendlySelectionFormDefinition();
        });

        this.frequencyPoolFormService.patchFormValue(
            this.frequencyPoolOptionsControl,
            response.frequencyPoolOptions,
        );

        this.includeFrequencyForValidationFormService.patchFormValue(
            this.includeFrequenciesForValidationControl,
            response.includeFrequenciesForValidation,
        );

        this.setupFormHandlers();

        this.form.patchValue(response);

        this.setFrequencyAllocationCodeControlValue();

        this.updateGrid(CoordinationParametersGridType.frequencyPool);
        this.updateGrid(
            CoordinationParametersGridType.includeFrequenciesForValidation,
        );
        this.updateGrid(CoordinationParametersGridType.friendlySelection);
    }

    /**
     * Sets the Frequency Allocation Code control value based on the frequency pool options.
     */
    private setFrequencyAllocationCodeControlValue(): void {
        const frequencyAllocationCodes =
            this.frequencyPoolFormService.getDistinctFrequencyAllocationCodes(
                this.frequencyPoolOptionsControl,
            );

        if (frequencyAllocationCodes.length > 1) {
            throw new Error(
                'Frequency Allocation Codes must be the same for all frequency pool options',
            );
        }

        (this.form.controls.frequencyAllocationCode as FormControl).setValue(
            frequencyAllocationCodes[0],
        );
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<CoordinationParametersResponse> {
        return this.coordinationService.getCoordinationParameters(
            applicationId,
        );
    }

    protected override saveFormDataToApi(
        request: CoordinationParametersRequest,
    ): Observable<ValidationResult> {
        return this.coordinationService.saveCoordinationParameters(request);
    }

    protected override generateRequestObject(): CoordinationParametersRequest {
        const formValue = this.form
            .value as Required<CoordinationParametersRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
